import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Check } from 'lucide-react';

type DataItem = {
  value: string;
  label: string;
};
type ComboboxProps = {
  data: DataItem[];
  onChange?: (value: DataItem[]) => void;
  value?: DataItem[];
  placeholder?: string;
};

export function ComboBoxResponsive({
  data,
  onChange,
  value,
  placeholder = 'Choose one',
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [selected, setSelected] = React.useState<DataItem[] | null>(value);

  const onUpdate = (value: DataItem[]) => {
    setSelected(value);
    if (typeof onChange !== 'undefined') {
      onChange(value);
    }
  };

  const renderTriggerContent = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} items selected`;
  };

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="justify-start">
            {selected ? <>{renderTriggerContent()}</> : <>{placeholder}</>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <ComboList
            selected={selected}
            setOpen={setOpen}
            setSelected={onUpdate}
            data={data}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="justify-start">
          {selected ? <>{renderTriggerContent()}</> : <>{placeholder}</>}
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          <ComboList
            selected={selected}
            setOpen={setOpen}
            setSelected={onUpdate}
            data={data}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function ComboList({
  selected,
  setOpen,
  setSelected,
  data,
}: {
  selected: DataItem[];
  setOpen: (open: boolean) => void;
  setSelected: (status: DataItem[] | null) => void;
  data: DataItem[];
}) {
  return (
    <Command>
      <CommandInput placeholder="Filter status..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {data.map((status) => {
            const idx = selected.findIndex(
              (priority) => priority.value === status.value,
            );
            return (
              <CommandItem
                key={status.value}
                value={status.value}
                onSelect={(value) => {
                  const idx = selected.findIndex(
                    (priority) => priority.value === value,
                  );

                  if (idx === -1) {
                    setSelected([
                      ...selected,
                      data.find((priority) => priority.value === value),
                    ]);
                  } else {
                    const newSelected = [...selected];
                    newSelected.splice(idx, 1);
                    setSelected(newSelected);
                  }
                }}
              >
                {idx !== -1 && <Check className="h-3 w-3 mr-2" />}
                {status.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
